/**
 * Point & Figure Chart Calculator — método High/Low de A.W. Cohen (canônico)
 *
 * Referências (issues #182 / #82):
 * - StockCharts ChartSchool, "Introduction to Point & Figure Charts" (exemplo
 *   numérico de 6 dias usado como gabarito no teste canônico).
 * - Fonte REAL do HTML antigo: `CIQ.ChartEngine.calculatePointFigure`
 *   (HtmlChart/wwwroot/js/chart/chartiq.js, decodificado) — paridade "como no
 *   HTML antigo" pedida no #82: tabela de box automática + regra one-step-back.
 *
 * Regras implementadas:
 * - Grade ABSOLUTA: níveis de box são múltiplos do boxSize (box "13" = faixa 13–13.99).
 * - Coluna X (alta): novo X quando high ≥ próximo nível; preenche TODOS os níveis
 *   atingidos na mesma barra; enquanto der X, o low é ignorado (prioridade Cohen).
 * - Coluna O (baixa): simétrico, com o low.
 * - Reversão: só se não houve continuação; dispara com IGUALDADE
 *   (low ≤ topo − reversal×box, ou high ≥ fundo + reversal×box). A nova coluna
 *   começa um box aquém do extremo e preenche até o nível atingido.
 * - boxSize null = automático via TABELA DE ESCALA TRADICIONAL do ChartIQ
 *   (chave = último Close; ÷10 em intraday) — idêntico ao HTML antigo.
 * - one-step-back (pfStepBack do ChartIQ): quando uma reversão cria uma coluna de
 *   um único box e o preço reverte de novo, a coluna-de-um-box é convertida no
 *   lugar em vez de virar zigue-zague. Só é alcançável em reversal=1 (em
 *   reversal≥2 a reversão exige ≥2 boxes → nunca é coluna única → sem efeito).
 */

import { PointAndFigure as defaultOptions } from "./defaultOptionsForComputation";

// Teto de segurança: boxSize minúsculo (ex.: 0.01 num índice) geraria centenas de
// milhares de boxes por recálculo → freeze.
//
// O teto conta marks ACUMULADOS em todo o histórico, então cresce com
// nº de barras × volatilidade ÷ box. Calibrado em 10.000 ele barrava o caso
// legítimo (#432): WINFUT 15min, 2.000 barras, box 50 da própria tabela
// tradicional → 12.769 marks em 10,8 ms, e o gráfico saía VAZIO. Medições no
// feed real (2026-07-27, 2.000 barras, range 18.417 pts):
//
//   box=50 → 12.769 marks / 10,8 ms      box=10 → 56.037 marks / 25,6 ms
//   box=25 → 21.673 marks / 13,9 ms      box=1  → 570.763 marks / 238 ms
//                                        box=0,5 → 1.142.459 marks / 705 ms
//
// A série desenha só `plotData` (janela visível), não todas as colunas — o custo
// do teto é materialização, não render. 50.000 (~25 ms) mantém a proteção contra
// o freeze real e libera todo uso normal.
const MAX_TOTAL_BOXES = 50000;

// Modo automático: se nem o teto acima comportar o histórico, o box da tabela é
// dobrado até caber (#432) — melhor um box maior que gráfico em branco. Limite de
// passos só para não virar loop infinito com dado patológico.
const MAX_AUTO_SCALE_STEPS = 12;

// Calculadora roda a cada quote batch — warns de dado degenerado precisam ser
// warn-once para não inundar o console (regra de performance do projeto).
const warned = {};
function warnOnce(key, msg) {
  if (!warned[key]) {
    warned[key] = true;
    console.warn(msg);
  }
}

// Barras diárias+ ficam ≥ ~20h apart; intraday (min/hora) muito menos. Usa a
// mediana dos gaps para separar (robusto a fins de semana/feriados).
const DAILY_THRESHOLD_MS = 20 * 60 * 60 * 1000;
function isDailyData(rawData) {
  const n = rawData.length;
  if (n < 3) return true; // sem evidência → assume diário (caso comum p/ P&F)
  const sample = Math.min(50, n - 1);
  const diffs = [];
  for (let i = n - sample; i < n; i++) {
    const t1 = +new Date(rawData[i].date);
    const t0 = +new Date(rawData[i - 1].date);
    if (isFinite(t1) && isFinite(t0) && t1 > t0) diffs.push(t1 - t0);
  }
  if (!diffs.length) return true;
  diffs.sort((a, b) => a - b);
  const median = diffs[Math.floor(diffs.length / 2)];
  return median >= DAILY_THRESHOLD_MS;
}

// Tabela de escala tradicional do ChartIQ (calculatePointFigure), chave = último
// Close. Intraday usa 1/10 do box diário. É a fonte literal do "como no HTML antigo".
// (A tabela Forex do ChartIQ não se aplica aos símbolos B3 — omitida de propósito.)
// Exportada p/ o pill de configuração (PointAndFigureBoxIndicator) exibir o box
// efetivo do modo auto sem duplicar a tabela.
export function chartIQBoxSize(rawData) {
  const last = rawData[rawData.length - 1];
  const px = last.close != null ? last.close : last.high;
  let box;
  if (px) {
    if (px < 0.25) box = 0.0625;
    else if (px < 1) box = 0.125;
    else if (px < 5) box = 0.25;
    else if (px < 20) box = 0.5;
    else if (px < 100) box = 1;
    else if (px < 200) box = 2;
    else if (px < 500) box = 4;
    else if (px < 1000) box = 5;
    else if (px < 25000) box = 50;
    else box = 500;
  } else {
    box = 1;
  }
  if (!isDailyData(rawData)) box /= 10;
  return box;
}

// Box efetivamente usado no modo automático: normalmente o da tabela, mas pode
// ter sido escalado pelo teto de marks (#432). O calculator publica aqui o valor
// que de fato desenhou, chaveado pelo próprio array de barras (WeakMap → não
// segura referência), para o pill exibir o mesmo número que está no gráfico.
// Sem entrada no cache (pill montado antes do 1º cálculo) cai na tabela.
const autoBoxUsed = new WeakMap();

export function effectiveAutoBoxSize(rawData) {
  if (!rawData || rawData.length === 0) return 1;
  const used = autoBoxUsed.get(rawData);
  return used != null ? used : chartIQBoxSize(rawData);
}

// Nº de casas decimais para arredondar os níveis da grade (evita poeira de float).
function boxDecimals(box) {
  const s = box.toString();
  const dot = s.indexOf(".");
  return Math.min((dot < 0 ? 0 : s.length - dot - 1) + 2, 10);
}

// Aplica os flags de calendário de uma barra à coluna, em cascata ano>tri>mês>sem
// (mesma prioridade do antigo updateColumns) — define a data do marco do eixo X.
function applyFlags(col, d, dt) {
  if (d.startOfYear && !col.startOfYear) {
    col.startOfYear = true;
    col.startOfQuarter = d.startOfQuarter;
    col.startOfMonth = d.startOfMonth;
    col.startOfWeek = d.startOfWeek;
    col.date = dt;
  } else if (d.startOfQuarter && col.startOfQuarter !== true) {
    col.startOfQuarter = true;
    col.startOfMonth = d.startOfMonth;
    col.startOfWeek = d.startOfWeek;
    col.date = dt;
  } else if (d.startOfMonth && col.startOfMonth !== true) {
    col.startOfMonth = true;
    col.startOfWeek = d.startOfWeek;
    col.date = dt;
  } else if (d.startOfWeek && col.startOfWeek !== true) {
    col.startOfWeek = true;
    col.date = dt;
  }
}

const Q2 = 1e-8;

export default function () {
  let options = defaultOptions;
  let dateAccessor = (d) => d.date;
  let dateMutator = (d, date) => {
    d.date = date;
  };

  // Motor: port fiel do `CIQ.ChartEngine.calculatePointFigure` (chartiq.js do
  // HTML antigo, decodificado). Cada coluna é {pfOpen, pfClose, pfTrend,
  // pfStepBack} + high/low/close/volume/datas agregados. A saída é convertida
  // para {boxes:[{open,close}], direction} que a série consome.
  function calculator(rawData) {
    if (!rawData || rawData.length === 0) {
      warnOnce("noData", "P&F: No data to process");
      return [];
    }

    const { boxSize: configBoxSize, reversal } = options;
    const isAuto = configBoxSize === null;
    const tableBox = isAuto
      ? chartIQBoxSize(rawData)
      : configBoxSize > 0
        ? configBoxSize
        : 1;

    let box = tableBox;
    let cols = buildColumns(rawData, box, reversal);

    if (countBoxes(cols, box) > MAX_TOTAL_BOXES) {
      if (!isAuto) {
        // Box fixo: é escolha explícita do usuário — não dá para "corrigir" sem
        // desobedecer. Recusa (o aviso visível fica a cargo da UI, ver #432).
        warnOnce(
          `overflow:${box}`,
          `P&F: mais de ${MAX_TOTAL_BOXES} boxes (box fixo ${box} pequeno demais para o histórico) — gráfico vazio`,
        );
        return [];
      }
      // Automático: dobra o box até caber. Desvio consciente do HtmlCharts5
      // (que não tem teto) — só ocorre onde a alternativa seria tela em branco.
      let steps = 0;
      while (
        countBoxes(cols, box) > MAX_TOTAL_BOXES &&
        steps < MAX_AUTO_SCALE_STEPS
      ) {
        box *= 2;
        steps++;
        cols = buildColumns(rawData, box, reversal);
      }
      if (countBoxes(cols, box) > MAX_TOTAL_BOXES) {
        warnOnce(
          `overflowAuto:${tableBox}`,
          `P&F: histórico não cabe em ${MAX_TOTAL_BOXES} boxes nem com box ${box} — gráfico vazio`,
        );
        return [];
      }
      warnOnce(
        `autoscale:${tableBox}->${box}`,
        `P&F: box automático ${tableBox} → ${box} para caber o histórico em ${MAX_TOTAL_BOXES} boxes`,
      );
    }

    if (isAuto) autoBoxUsed.set(rawData, box);

    return materialize(cols, box, dateMutator);
  }

  // Motor propriamente dito: percorre as barras e devolve as colunas ChartIQ
  // ({pfOpen, pfClose, pfTrend, pfStepBack} + agregados). Parametrizado pelo box
  // porque o modo automático pode precisar reexecutar com box maior (#432).
  function buildColumns(rawData, box, reversal) {
    const b9 = boxDecimals(box);
    const snapHi = (p) => Number((Math.floor(p / box + Q2) * box).toFixed(b9));
    const snapLo = (p) => Number((Math.ceil(p / box - Q2) * box).toFixed(b9));
    const g = reversal * box;

    const cols = [];
    let acc = 0; // volume acumulado por barras puladas (pré-semeadura)
    let pendingFlags = null;

    const collectPending = (d, dt) => {
      pendingFlags = pendingFlags || { date: dt };
      pendingFlags.startOfYear = pendingFlags.startOfYear || d.startOfYear;
      pendingFlags.startOfQuarter =
        pendingFlags.startOfQuarter || d.startOfQuarter;
      pendingFlags.startOfMonth = pendingFlags.startOfMonth || d.startOfMonth;
      pendingFlags.startOfWeek = pendingFlags.startOfWeek || d.startOfWeek;
    };

    // toca a coluna corrente: agrega high/low/close/volume/flags da barra
    const touch = (col, d, dt, w8, H3, P8, vol) => {
      col.high = Math.max(w8, col.high);
      col.low = Math.min(H3, col.low);
      col.close = P8;
      col.volume += vol;
      col.toDate = dt;
      applyFlags(col, d, dt);
    };

    for (let i = 0; i < rawData.length; i++) {
      const d = rawData[i];
      if (!d) continue;
      acc += d.volume || 0;
      const dt = dateAccessor(d);
      const P8 = d.close;
      let y8 = d.open,
        w8 = d.high,
        H3 = d.low;
      y8 = y8 || y8 === 0 ? y8 : P8;
      w8 = w8 || w8 === 0 ? w8 : P8;
      H3 = H3 || H3 === 0 ? H3 : P8;

      // Guard NaN: barra corrompida não pode semear/mover a grade.
      if (!isFinite(w8) || !isFinite(H3)) {
        collectPending(d, dt);
        continue;
      }

      if (!cols.length) {
        // Semeadura na 1ª barra finita, direção pelo corpo do candle (mantida
        // por opção — o ChartIQ semeia sempre X; ver #82). Acumula desde já.
        const up = P8 >= y8;
        const hi = snapHi(w8);
        const lo = snapLo(H3);
        const seed = {
          pfOpen: up ? lo : hi,
          pfClose: up ? hi : lo,
          pfTrend: up ? "X" : "O",
          high: w8,
          low: H3,
          close: P8,
          volume: acc,
          date: (pendingFlags && pendingFlags.date) || dt,
          fromDate: dt,
          toDate: dt,
          startOfYear: false,
          startOfQuarter: false,
          startOfMonth: false,
          startOfWeek: false,
        };
        if (pendingFlags) {
          applyFlags(seed, pendingFlags, seed.date);
          pendingFlags = null;
        }
        applyFlags(seed, d, dt);
        if (seed.pfOpen === seed.pfClose) seed.pfStepBack = "-";
        cols.push(seed);
        acc = 0;
        continue;
      }

      const I = cols[cols.length - 1];
      if (I.pfTrend === "O") {
        if (H3 <= I.pfClose - box) {
          // Continuação O (desce)
          I.pfClose = snapLo(H3);
          if (I.pfStepBack === "O") I.pfStepBack = null;
          touch(I, d, dt, w8, H3, P8, acc);
        } else if (w8 >= I.pfClose + g) {
          // Reversão O→X — s5 CRU (sem arredondar), fiel ao binário vivo:
          // `a7C = P7C.pfClose + W7C`. O gatilho do one-step-back (s5 === z7)
          // DEVE falhar na poeira de float igual ao vivo — com box "sujo" em
          // binário (0.05, 0.1…) e reversal=1 o ChartIQ NÃO colapsa a coluna
          // (prova: tools/htmlchart-oracle/PROVA-PF.md, repro de 3 barras).
          const s5 = I.pfClose + box;
          const z7 = snapHi(w8);
          if (I.pfStepBack === "O") {
            // one-step-back: converte a coluna-de-um-box no lugar
            I.pfOpen = s5;
            I.pfClose = z7;
            I.pfTrend = "X";
            touch(I, d, dt, w8, H3, P8, acc);
          } else {
            const r = mkColumn("X", s5, z7, d, dt, w8, H3, P8, acc);
            if (s5 === z7) r.pfStepBack = "X";
            cols.push(r);
          }
        } else {
          touch(I, d, dt, w8, H3, P8, acc);
        }
        acc = 0;
      } else {
        // I.pfTrend === "X"
        if (w8 >= I.pfClose + box) {
          // Continuação X (sobe)
          I.pfClose = snapHi(w8);
          if (I.pfStepBack === "X" || I.pfStepBack === "-") I.pfStepBack = null;
          touch(I, d, dt, w8, H3, P8, acc);
        } else if (H3 <= I.pfClose - g) {
          // Reversão X→O — s5 CRU (sem arredondar), ver comentário da O→X.
          const s5 = I.pfClose - box;
          const z7 = snapLo(H3);
          if (I.pfStepBack === "X" || I.pfStepBack === "-") {
            I.pfOpen = s5;
            I.pfClose = z7;
            I.pfTrend = "O";
            touch(I, d, dt, w8, H3, P8, acc);
            if (s5 !== z7 && I.pfStepBack === "-") I.pfStepBack = null;
          } else {
            const r = mkColumn("O", s5, z7, d, dt, w8, H3, P8, acc);
            if (s5 === z7) r.pfStepBack = "O";
            cols.push(r);
          }
        } else {
          touch(I, d, dt, w8, H3, P8, acc);
        }
        acc = 0;
      }
    }

    function mkColumn(trend, pfOpen, pfClose, d, dt, w8, H3, P8, vol) {
      const col = {
        pfOpen,
        pfClose,
        pfTrend: trend,
        high: w8,
        low: H3,
        close: P8,
        volume: vol,
        date: dt,
        fromDate: dt,
        toDate: dt,
        startOfYear: false,
        startOfQuarter: false,
        startOfMonth: false,
        startOfWeek: false,
      };
      applyFlags(col, d, dt);
      return col;
    }

    return cols;
  }

  // Marks que a materialização geraria — barato o bastante para rodar antes de
  // alocar os boxes (é o que o teto mede).
  function countBoxes(cols, box) {
    let total = 0;
    for (const c of cols) {
      total += Math.round(Math.abs(c.pfClose - c.pfOpen) / box) + 1;
    }
    return total;
  }

  // Converte colunas ChartIQ → formato da série: um box por mark (gap de grade).
  function materialize(cols, box, dateMutator) {
    const b9 = boxDecimals(box);
    const round = (v) => Number((Math.round(v / box) * box).toFixed(b9));

    return cols.map((c) => {
      const dir = c.pfTrend === "X" ? 1 : -1;
      const boxes = [];
      if (dir > 0) {
        for (let L = c.pfOpen; L < c.pfClose - Q2; L = round(L + box)) {
          boxes.push({ open: L, close: round(L + box) });
        }
      } else {
        for (let L = c.pfOpen; L > c.pfClose + Q2; L = round(L - box)) {
          boxes.push({ open: L, close: round(L - box) });
        }
      }
      if (boxes.length === 0) {
        // Coluna de um único mark (pfStepBack não colapsado / seed degenerado):
        // desenha 1 box para não sumir do gráfico.
        boxes.push(
          dir > 0
            ? { open: c.pfClose, close: round(c.pfClose + box) }
            : { open: c.pfClose, close: round(c.pfClose - box) },
        );
      }
      dateMutator(c, c.date);
      return {
        direction: dir,
        boxes,
        open: c.pfOpen,
        close: c.pfClose,
        // y-extent justo à grade da coluna (como o antigo updateColumns), não ao
        // high/low bruto das barras — senão o eixo Y estoura além dos boxes.
        high: Math.max(c.pfOpen, c.pfClose),
        low: Math.min(c.pfOpen, c.pfClose),
        volume: c.volume,
        date: c.date,
        fromDate: c.fromDate,
        toDate: c.toDate,
        startOfYear: c.startOfYear,
        startOfQuarter: c.startOfQuarter,
        startOfMonth: c.startOfMonth,
        startOfWeek: c.startOfWeek,
      };
    });
  }
  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };
  calculator.dateMutator = function (x) {
    if (!arguments.length) return dateMutator;
    dateMutator = x;
    return calculator;
  };
  calculator.dateAccessor = function (x) {
    if (!arguments.length) return dateAccessor;
    dateAccessor = x;
    return calculator;
  };

  return calculator;
}
